import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Select, MenuItem, Button, FormControl, InputLabel
} from '@mui/material';
import { Visibility, Edit, Block } from '@mui/icons-material';

const PatientManagement = () => {
  const { adminUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [searchName, setSearchName] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortConsultations, setSortConsultations] = useState('desc');

  useEffect(() => {
    const fetchPatients = async () => {
      if (!adminUser) {
        navigate('/admin');
        return;
      }
      const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
      try {
        const res = await api.get('/admin/users', config);
        setPatients(res.data.patients || []);
      } catch (err) {
        console.error('Patients fetch error:', err.response?.data);
        if (err.response?.status === 401 || err.response?.status === 403) {
          navigate('/admin');
        }
      }
    };
    fetchPatients();
  }, [adminUser, navigate]);

  // Filter and sort patients
  const filteredPatients = patients
    .filter(patient => 
      patient.name.toLowerCase().includes(searchName.toLowerCase()) &&
      (!searchEmail || (patient.email && patient.email.toLowerCase().includes(searchEmail.toLowerCase()))) &&
      (!searchPhone || (patient.phone && patient.phone.includes(searchPhone))) &&
      (!filterCity || patient.city === filterCity) &&
      (!filterDate || new Date(patient.createdAt).toISOString().split('T')[0] === filterDate) &&
      (filterStatus === 'all' || patient.status === filterStatus)
    )
    .sort((a, b) => sortConsultations === 'desc' 
      ? (b.consultationCount || 0) - (a.consultationCount || 0) 
      : (a.consultationCount || 0) - (b.consultationCount || 0));

  const handleDeactivate = async (id) => {
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      await api.put(`/admin/users/${id}`, { status: 'inactive' }, config);
      setPatients(patients.map(p => p._id === id ? { ...p, status: 'inactive' } : p));
    } catch (err) {
      console.error('Deactivate error:', err.response?.data);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Patient Management</Typography>
      <Paper sx={{ p: 2 }}>
        <Box display="flex" gap={2} mb={2} flexWrap="wrap">
          <TextField 
            label="Search by Name" 
            value={searchName} 
            onChange={(e) => setSearchName(e.target.value)} 
            sx={{ minWidth: 200 }}
          />
          <TextField 
            label="Search by Email" 
            value={searchEmail} 
            onChange={(e) => setSearchEmail(e.target.value)} 
            sx={{ minWidth: 200 }}
          />
          <TextField 
            label="Search by Phone Number" 
            value={searchPhone} 
            onChange={(e) => setSearchPhone(e.target.value)} 
            sx={{ minWidth: 200 }}
          />
          <TextField 
            label="Filter by City" 
            value={filterCity} 
            onChange={(e) => setFilterCity(e.target.value)} // Fixed typo: setSearchCity -> setFilterCity
            sx={{ minWidth: 200 }}
          />
          <TextField 
            type="date" 
            label="Filter by Registration Date" 
            value={filterDate} 
            onChange={(e) => setFilterDate(e.target.value)} 
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
          />
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="banned">Banned</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Sort by Consultations</InputLabel>
            <Select value={sortConsultations} onChange={(e) => setSortConsultations(e.target.value)}>
              <MenuItem value="desc">Most Active</MenuItem>
              <MenuItem value="asc">Least Active</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Registration Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Consultations</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPatients.map((patient) => (
              <TableRow key={patient._id}>
                <TableCell>{patient.name}</TableCell>
                <TableCell>{patient.email || 'N/A'}</TableCell>
                <TableCell>{patient.phone || 'N/A'}</TableCell>
                <TableCell>{patient.city}</TableCell>
                <TableCell>{new Date(patient.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>{patient.status || 'active'}</TableCell>
                <TableCell>{patient.consultationCount || 0}</TableCell>
                <TableCell>
                  <Button startIcon={<Visibility />} onClick={() => navigate(`/admin/patients/${patient._id}`)}>View</Button>
                  <Button startIcon={<Edit />} onClick={() => navigate(`/admin/patients/${patient._id}/edit`)}>Edit</Button>
                  <Button startIcon={<Block />} onClick={() => handleDeactivate(patient._id)} disabled={patient.status === 'inactive'}>Deactivate</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default PatientManagement;